import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  Fingerprint,
  KeyRound,
  Lock,
  MonitorSmartphone,
  Plus,
  ShieldAlert,
  Siren,
  Timer,
  TriangleAlert,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT, useI18n, LANG_LABELS, type Lang } from '@/i18n'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { PlannedBadge } from '@/components/DataDisplay'
import { detectEnvironment } from '@/shell/AppShell'
import {
  AUTH_STR as S,
  fetchAdminDevices,
  fetchIpBlocks,
  type AdminDevice,
  type IpBlockRow,
} from './authAdminApi'
import {
  fetchRequests,
  requestAccess,
  fetchBreakGlass,
  type AccessRequest,
  type BreakGlassEvent,
} from '@/screens/jit/jitApi'
import { fetchRoles, type RoleRow } from '@/screens/staff/staffOrgApi'

/**
 * Small fail-soft loader for the A1 showcase screens: fetches a list once (and
 * on `reload`); an aborted/failed request lands on `[]` so the card shows an
 * honest empty state rather than crashing. `null` = still loading.
 */
function useAuthList<T>(fn: (s: AbortSignal) => Promise<T[]>): {
  data: T[] | null
  reload: () => void
} {
  const [data, setData] = useState<T[] | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setData(null)
    fn(ac.signal)
      .then((d) => {
        if (!ac.signal.aborted) setData(d)
      })
      .catch(() => {
        if (!ac.signal.aborted) setData([])
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])
  return { data, reload: () => setTick((t) => t + 1) }
}

/**
 * A1 — Autentifikatsiya va kirish (8 ekran).
 *
 * ⚠️ TZ A1.01/A1.02 SSO (`my.mygram.my`) + MAJBURIY WebAuthn deydi.
 * Ikkalasining ham provayderi yo'q (PROGRESS.md §4.2: «Admin SSO/WebAuthn —
 * provayder yo'q»). Jonli yadro faqat `POST /admin/login` (login+parol → admin
 * JWT, 8 soat) beradi.
 *
 * QAROR (foydalanuvchi bilan kelishilgan): mavjud JWT oqimi ustiga quriladi,
 * SSO/WebAuthn/break-glass ekranlari OCHIQ «rejalashtirilgan» belgisi bilan
 * ko'rsatiladi — ishlamaydigan narsa ishlaydi deb ko'rsatilmaydi.
 */

/* ── Umumiy karkas ────────────────────────────────────────────── */

function AuthLayout({
  icon,
  title,
  description,
  children,
  screenId,
  tone = 'neutral',
}: {
  icon: ReactNode
  title: string
  description?: string
  children: ReactNode
  screenId: string
  tone?: 'neutral' | 'danger' | 'warning'
}) {
  const { lang, setLang } = useI18n()
  const env = detectEnvironment()

  const ring =
    tone === 'danger' ? 'border-danger' : tone === 'warning' ? 'border-warning' : 'border-border-default'

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-0 p-6">
      {env !== 'prod' && (
        <div
          className="mb-4 rounded-sm px-2 py-0.5 text-xs font-semibold text-black uppercase"
          style={{ background: env === 'staging' ? 'var(--env-staging)' : 'var(--env-dev)' }}
        >
          {env}
        </div>
      )}

      <div
        className={cn('w-full max-w-[380px] rounded-lg border bg-surface-2 p-5', ring)}
        style={{ boxShadow: 'var(--shadow-panel)' }}
        data-screen={screenId}
      >
        <div className="mb-4 flex flex-col items-center gap-2 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-3">{icon}</div>
          <div>
            <h1 className="text-lg font-semibold text-primary">{title}</h1>
            {description && <p className="mt-0.5 text-sm text-secondary">{description}</p>}
          </div>
        </div>
        {children}
      </div>

      <div className="mt-4 flex items-center gap-1">
        {(Object.keys(LANG_LABELS) as Lang[]).map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLang(l)}
            className={cn(
              'rounded-sm px-2 py-0.5 text-xs transition-colors',
              lang === l ? 'text-primary' : 'text-tertiary hover:text-secondary',
            )}
          >
            {LANG_LABELS[l]}
          </button>
        ))}
        <span className="ml-2 font-mono text-xs text-disabled">{screenId}</span>
      </div>
    </div>
  )
}

/* ── A1.01 Kirish ─────────────────────────────────────────────── */

export function LoginScreen() {
  const t = useT()
  const { signIn } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [needTotp, setNeedTotp] = useState(false) // A28.02 — server asked for a 2FA code
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const userRef = useRef<HTMLInputElement>(null)
  const passRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    userRef.current?.focus()
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    // MUHIM: qiymatlar DOM'dan o'qiladi, React holatidan emas.
    // Chrome/parol-menejeri avto-to'ldirganda `onChange` ISHGA TUSHMAYDI —
    // holat bo'sh qoladi va admin parol menejeri bilan kira olmaydi.
    // (Brauzerda sinab topilgan nuqson.)
    const u = (userRef.current?.value ?? username).trim()
    const p = passRef.current?.value ?? password
    if (!u || !p) {
      setError(t.auth.invalidCredentials)
      return
    }

    setBusy(true)
    setError(null)
    try {
      await signIn(u, p, needTotp ? totp.trim() : undefined)
    } catch (err) {
      // A28.02 — parol to'g'ri, lekin 2FA kodi kerak/xato: kod maydonini ko'rsatamiz.
      if (err instanceof ApiError && err.code === 'TWO_FACTOR_REQUIRED') {
        setNeedTotp(true)
        setError(needTotp ? t.auth.twoFactorInvalid : null)
        setTotp('')
      } else if (err instanceof ApiError && err.code === 'NETWORK') setError(t.auth.networkError)
      else if (err instanceof ApiError && err.status === 403) setError(t.auth.ipDeniedTitle)
      else {
        setError(t.auth.invalidCredentials)
        setPassword('')
        setNeedTotp(false)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      screenId="A1.01"
      icon={<KeyRound size={20} className="text-accent" />}
      title={t.auth.signInTitle}
      description={t.auth.signInSubtitle}
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label={t.auth.username} htmlFor="u" required>
          <Input
            id="u"
            ref={userRef}
            value={username}
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value)}
          />
        </Field>

        <Field label={t.auth.password} htmlFor="p" required>
          <Input
            id="p"
            ref={passRef}
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        {needTotp && (
          <Field label={t.auth.twoFactorCode} htmlFor="totp" required>
            <Input
              id="totp"
              value={totp}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              autoFocus
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
        )}

        {error && (
          <p className="flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-2 py-1.5 text-sm text-danger">
            <TriangleAlert size={13} className="shrink-0" />
            {error}
          </p>
        )}

        {/* Faqat `busy` bo'yicha o'chiriladi — bo'shlik tekshiruvi `onSubmit` da,
            aks holda avto-to'ldirilgan forma yuborib bo'lmaydi. */}
        <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full justify-center">
          {busy ? t.auth.signingIn : t.auth.signIn}
        </Button>
      </form>

      {/* A1.01 SSO + A1.02 WebAuthn — provayder yo'q, ochiq belgilanadi */}
      <div className="mt-4 space-y-2 border-t border-border-subtle pt-3">
        <PlannedRow icon={<Fingerprint size={13} />} title={t.auth.webauthnTitle} desc={t.auth.webauthnDesc} id="A1.02" />
        <PlannedRow icon={<KeyRound size={13} />} title={t.auth.ssoTitle} desc={t.auth.ssoDesc} id="A1.01" />
      </div>
    </AuthLayout>
  )
}

function PlannedRow({
  icon,
  title,
  desc,
  id,
}: {
  icon: ReactNode
  title: string
  desc: string
  id: string
}) {
  return (
    <div className="flex gap-2 rounded-md border border-dashed border-border-default px-2.5 py-2">
      <span className="mt-0.5 shrink-0 text-tertiary">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-secondary">{title}</span>
          <PlannedBadge />
          <span className="font-mono text-xs text-disabled">{id}</span>
        </div>
        <p className="mt-0.5 text-xs text-tertiary">{desc}</p>
      </div>
    </div>
  )
}

/* ── A1.05 Sessiya muddati tugadi ─────────────────────────────── */

export function SessionExpiredScreen({ onContinue }: { onContinue: () => void }) {
  const t = useT()
  return (
    <AuthLayout
      screenId="A1.05"
      tone="warning"
      icon={<Timer size={20} className="text-warning" />}
      title={t.auth.sessionExpiredTitle}
      description={t.auth.sessionExpiredDesc}
    >
      <Button variant="primary" size="lg" className="w-full justify-center" onClick={onContinue}>
        {t.auth.sessionExpiredAction}
      </Button>
    </AuthLayout>
  )
}

/* ── Qulflangan (30 daqiqa faoliyatsizlik, TZ A1) ─────────────── */

export function LockScreen() {
  const t = useT()
  const { identity, unlock, signOut } = useAuth()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    // LoginScreen dagi kabi — avto-to'ldirish React holatini yangilamaydi.
    const p = ref.current?.value ?? password
    if (!p) return
    setBusy(true)
    setError(null)
    try {
      await unlock(p)
    } catch {
      setError(t.auth.invalidCredentials)
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      screenId="A1"
      icon={<Lock size={20} className="text-warning" />}
      title={t.auth.idleLockedTitle}
      description={t.auth.idleLockedDesc}
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <p className="text-center text-sm text-secondary">{identity?.username}</p>
        <Field label={t.auth.password} htmlFor="lock-p">
          <Input
            id="lock-p"
            ref={ref}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" variant="primary" size="lg" className="w-full justify-center" disabled={busy}>
          {t.auth.unlock}
        </Button>
        <Button variant="ghost" size="sm" className="w-full justify-center" onClick={signOut}>
          {t.common.signOut}
        </Button>
      </form>
    </AuthLayout>
  )
}

/* ── A1.03 Qurilmani ro'yxatdan o'tkazish (REAL) ──────────────────
   Admin auth = JWT (stateless, sessiya jadvali yo'q). Shu sabab "qurilmalar"
   = joriy adminning HAQIQIY kirish-nuqtalari: audit izidan (admin_audit) IP
   bo'yicha guruhlangan — IP, birinchi/oxirgi kirish, amallar soni + joriy-IP
   belgisi. GET /admin/account/devices. Hech narsa soxta emas (bo'sh → honest). */

export function DeviceRegisterScreen() {
  const t = useT()
  const { data } = useAuthList<AdminDevice>(fetchAdminDevices)
  return (
    <AuthLayout
      screenId="A1.03"
      icon={<MonitorSmartphone size={20} className="text-accent" />}
      title={t.auth.deviceTitle}
      description={t.auth.deviceDesc}
    >
      {data === null ? (
        <p className="py-4 text-center text-sm text-tertiary">{S.loading}</p>
      ) : data.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-default p-3 text-center text-sm text-tertiary">
          {S.deviceNone}
        </p>
      ) : (
        <ul className="space-y-2">
          {data.map((d) => (
            <li key={d.ip} className="rounded-md border border-border-default bg-surface-1 p-2.5">
              <div className="flex items-center gap-2">
                <MonitorSmartphone size={14} className="shrink-0 text-tertiary" />
                <span className="font-mono text-sm text-primary">{d.ip}</span>
                {d.current && (
                  <span className="rounded-sm bg-success-subtle px-1.5 py-0.5 text-xs font-medium text-success">
                    {S.deviceCurrent}
                  </span>
                )}
                <span className="ml-auto text-xs text-tertiary">
                  {d.actions} {S.deviceActions}
                </span>
              </div>
              <p className="mt-1 text-xs text-tertiary">
                {S.deviceLastSeen}: {new Date(d.last_seen).toLocaleString()}
              </p>
            </li>
          ))}
        </ul>
      )}
    </AuthLayout>
  )
}

/* ── A1.04 Favqulodda kirish (break-glass) (REAL) ─────────────────
   MAVJUD JIT/break-glass yadrosiga ulanadi (/admin/jit/*): joriy faol
   privilegiyalangan grantlar (four-eyes tasdiqlangan) + break-glass jurnali
   + "huquq so'rash" amali (POST /admin/jit/requests). */

export function BreakGlassScreen() {
  const t = useT()
  const { toast } = useToast()
  const grants = useAuthList<AccessRequest>((s) => fetchRequests('approved', s))
  const bg = useAuthList<BreakGlassEvent>(fetchBreakGlass)
  const [scope, setScope] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!scope.trim() || busy) return
    setBusy(true)
    try {
      await requestAccess(scope.trim(), reason.trim())
      setScope('')
      setReason('')
      toast(S.jitRequested, { tone: 'success' })
      grants.reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const active = (grants.data ?? []).filter((g) => g.active)

  return (
    <AuthLayout
      screenId="A1.04"
      tone="danger"
      icon={<Siren size={20} className="text-danger" />}
      title={t.auth.breakGlassTitle}
      description={t.auth.breakGlassDesc}
    >
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-md border border-danger bg-danger-subtle p-2.5">
          <TriangleAlert size={14} className="mt-0.5 shrink-0 text-danger" />
          <p className="text-sm text-secondary">{t.auth.breakGlassWarning}</p>
        </div>

        {/* HAQIQIY huquq so'rash (four-eyes) */}
        <div className="space-y-2">
          <Field label={S.jitScope} htmlFor="bg-scope">
            <Input id="bg-scope" value={scope} onChange={(e) => setScope(e.target.value)} placeholder="db_browser" />
          </Field>
          <Field label={S.jitReason} htmlFor="bg-reason">
            <Input id="bg-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <Button
            variant="danger"
            size="lg"
            className="w-full justify-center"
            disabled={busy || !scope.trim()}
            onClick={submit}
          >
            <Plus size={14} />
            {S.jitRequest}
          </Button>
        </div>

        {/* Faol grantlar */}
        <div>
          <p className="mb-1 text-xs font-medium text-tertiary">{S.jitActiveGrants}</p>
          {grants.data === null ? (
            <p className="py-2 text-center text-sm text-tertiary">{S.loading}</p>
          ) : active.length === 0 ? (
            <p className="rounded-md border border-dashed border-border-default p-2.5 text-center text-sm text-tertiary">
              {S.jitNone}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {active.map((g) => (
                <li key={g.id} className="rounded-md border border-border-default bg-surface-1 p-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-primary">{g.scope}</span>
                    <span className="text-xs text-success">●</span>
                    {g.expires_at && (
                      <span className="ml-auto text-xs text-tertiary">
                        {new Date(g.expires_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {g.reason && <p className="mt-0.5 text-xs text-secondary">{g.reason}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Break-glass jurnali (oxirgilar) */}
        {(bg.data?.length ?? 0) > 0 && (
          <div>
            <p className="mb-1 text-xs font-medium text-tertiary">{S.jitBreakGlassLog}</p>
            <ul className="space-y-1">
              {(bg.data ?? []).slice(0, 5).map((e) => (
                <li key={e.id} className="flex items-center gap-2 text-xs text-tertiary">
                  <span className="font-mono text-secondary">{e.scope}</span>
                  <span className="ml-auto">{new Date(e.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </AuthLayout>
  )
}

/* ── A1.06 IP cheklovi (REAL) ─────────────────────────────────────
   MAVJUD IP-blok reyestriga ulanadi (/admin/ip-blocks, A8.16): bloklangan
   IP/subnet qoidalari — qiymat, sabab, urinishlar (hits). Bo'sh → honest. */

export function IpDeniedScreen() {
  const t = useT()
  const { data } = useAuthList<IpBlockRow>(fetchIpBlocks)
  return (
    <AuthLayout
      screenId="A1.06"
      tone="danger"
      icon={<ShieldAlert size={20} className="text-danger" />}
      title={t.auth.ipDeniedTitle}
      description={t.auth.ipDeniedDesc}
    >
      <div className="space-y-2">
        <p className="text-xs font-medium text-tertiary">{S.ipBlocksTitle}</p>
        {data === null ? (
          <p className="py-4 text-center text-sm text-tertiary">{S.loading}</p>
        ) : data.length === 0 ? (
          <p className="rounded-md border border-dashed border-border-default p-3 text-center text-sm text-tertiary">
            {S.ipBlocksNone}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {data.map((b) => (
              <li key={b.id} className="rounded-md border border-border-default bg-surface-1 p-2.5">
                <div className="flex items-center gap-2">
                  <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-tertiary">
                    {b.kind}
                  </span>
                  <span className="font-mono text-sm text-primary">{b.value}</span>
                  <span className="ml-auto text-xs text-tertiary">
                    {b.hits} {S.ipHits}
                  </span>
                </div>
                {b.reason && <p className="mt-0.5 text-xs text-secondary">{b.reason}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </AuthLayout>
  )
}

/* ── A1.07 Rol tanlash (REAL) ─────────────────────────────────────
   Jonli rol katalogiga ulanadi (GET /admin/roles): rol nomi, huquqlar soni,
   "hamma huquq" / "tayinlanadigan" belgilari. */

export function RoleSelectScreen() {
  const t = useT()
  const { data } = useAuthList<RoleRow>(fetchRoles)
  return (
    <AuthLayout
      screenId="A1.07"
      icon={<KeyRound size={20} className="text-accent" />}
      title={t.auth.roleSelectTitle}
      description={t.auth.roleSelectDesc}
    >
      {data === null ? (
        <p className="py-4 text-center text-sm text-tertiary">{S.loading}</p>
      ) : data.length === 0 ? (
        <p className="rounded-md border border-dashed border-border-default p-3 text-center text-sm text-tertiary">
          {S.roleNone}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {data.map((r) => (
            <li
              key={r.role}
              className="rounded-md border border-border-default px-3 py-2 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-primary">{r.role}</span>
                {r.all && (
                  <span className="rounded-sm bg-accent-subtle px-1.5 py-0.5 text-xs text-accent">
                    {S.roleAll}
                  </span>
                )}
                {r.assignable && (
                  <span className="rounded-sm bg-success-subtle px-1.5 py-0.5 text-xs text-success">
                    {S.roleAssignable}
                  </span>
                )}
                <span className="ml-auto text-xs text-tertiary">
                  {r.permission_count} {S.rolePermissions}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </AuthLayout>
  )
}
